
import React, { useState } from 'react';
import { SplitRequest, BookingDetails, FriendStatus } from '../types';
import Logo from './Logo';
import { useIsCompact } from '../hooks/useIsCompact';
import { useWebPaymentsEnabled } from '../hooks/useWebPaymentsEnabled';

interface Props {
  request: SplitRequest;
  booking: BookingDetails;
  onSimulatePaid: (id: string) => void;
  onUpdateShare: (id: string, amount: number) => void;
  onFriendView: (name: string) => void;
  onBackToHome: () => void;
}

const CleroSession: React.FC<Props> = ({ request, booking, onSimulatePaid, onUpdateShare, onFriendView, onBackToHome }) => {
  const isCompact = useIsCompact();
  const [toast, setToast] = useState<string | null>(null);
  const webPaymentsEnabled = useWebPaymentsEnabled();
  const [isPayoutSet, setIsPayoutSet] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState<'options' | 'success'>('options');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [tempAmount, setTempAmount] = useState<string>('');

  const totalPaidCount = request.friends.filter(f => f.status === 'paid').length + 1;
  const totalCollected = (request.amountPerPerson * totalPaidCount);
  const isFullyPaid = totalPaidCount === (request.friends.length + 1);

  const triggerToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  const handleSimulateItem = (friend: FriendStatus) => {
    if (friend.status === 'paid') return;
    onSimulatePaid(friend.id);
    const msg = isPayoutSet
      ? `${friend.name} paid $${friend.amount.toFixed(0)} — sent to your bank.`
      : `${friend.name} has paid.`;
    triggerToast(msg);
  };

  const finishOnboarding = () => {
    setOnboardingStep('success');
    setTimeout(() => {
      setIsPayoutSet(true);
      setShowOnboarding(false);
    }, 1500);
  };

  const startEditing = (friend: FriendStatus) => {
    setEditingId(friend.id);
    setTempAmount(friend.amount.toFixed(0));
  };

  const saveEdit = () => {
    if (editingId) {
      onUpdateShare(editingId, parseFloat(tempAmount) || 0);
      const friend = request.friends.find(f => f.id === editingId);
      triggerToast(`Amount updated for ${friend?.name}. Notification sent.`);
      setEditingId(null);
    }
  };

  return (
    <div className={`min-h-screen bg-white ${isCompact ? 'py-4 px-3' : 'py-12 px-4 md:px-8'} selection:bg-black selection:text-white relative font-sans`}>
      {toast && (
        <div className={`fixed ${isCompact ? 'top-4' : 'top-8'} left-1/2 -translate-x-1/2 z-[200] bg-gray-900 text-white px-6 py-2 rounded-full text-[10px] font-medium shadow-xl animate-in fade-in slide-in-from-top-4`}>
          {toast}
        </div>
      )}

      {/* Reverted Subtle Payout Banner */}
      {!isPayoutSet && (
        <div className={isCompact ? 'max-w-full mb-6' : 'max-w-4xl mx-auto mb-10'}>
          <div className={`bg-indigo-50 border border-indigo-100 rounded-2xl ${isCompact ? 'p-3' : 'p-4'} flex items-center justify-between animate-in slide-in-from-top-4`}>
            <div className="flex items-center space-x-3">
              <div className={`${isCompact ? 'w-6 h-6' : 'w-8 h-8'} rounded-lg bg-indigo-600 flex items-center justify-center text-white flex-shrink-0`}>
                <i className="fas fa-wallet text-[10px]"></i>
              </div>
              <div className="min-w-0">
                <p className={`${isCompact ? 'text-xs' : 'text-sm'} font-semibold text-indigo-900 leading-none mb-1 truncate`}>Reimburse automatically</p>
                {!isCompact && <p className="text-[11px] text-indigo-700 opacity-80">Add where Clero should send your money.</p>}
              </div>
            </div>
            <button
              onClick={() => setShowOnboarding(true)}
              className={`bg-indigo-600 text-white ${isCompact ? 'px-3 py-1.5 text-[10px]' : 'px-4 py-2 text-xs'} font-bold rounded-xl hover:bg-indigo-700 transition-colors flex-shrink-0`}
            >
              Set up
            </button>
          </div>
        </div>
      )}

      {showOnboarding && (
        <div className="fixed inset-0 z-[250] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-[2rem] w-full max-w-md overflow-hidden shadow-2xl animate-in zoom-in duration-300 p-10 space-y-8">
            {onboardingStep === 'options' ? (
              <>
                <div className="space-y-3 text-center">
                  <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-white mx-auto shadow-lg mb-2">
                    <i className="fas fa-bolt text-xl"></i>
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-xl font-bold text-gray-900 tracking-tight">Payout method</h3>
                    <p className="text-sm text-gray-500 font-medium leading-relaxed px-4">
                      Clero securely sends you money through your linked payout account. Choose how you'd like to be paid.
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={finishOnboarding}
                    className="w-full bg-white border border-gray-100 p-5 rounded-2xl text-left hover:border-indigo-600 hover:bg-indigo-50/50 transition-all group"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-bold text-gray-900">Instant payout to debit card</p>
                        <p className="text-[10px] text-indigo-400 font-bold tracking-widest uppercase">Immediate • Recommended</p>
                      </div>
                      <i className="fas fa-chevron-right text-gray-200 group-hover:text-indigo-600 text-[10px]"></i>
                    </div>
                  </button>
                  <button
                    onClick={finishOnboarding}
                    className="w-full bg-white border border-gray-100 p-5 rounded-2xl text-left hover:border-indigo-600 hover:bg-indigo-50/50 transition-all group"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-bold text-gray-900">Direct deposit to bank</p>
                        <p className="text-[10px] text-gray-400 font-bold tracking-widest uppercase">1–2 business days</p>
                      </div>
                      <i className="fas fa-chevron-right text-gray-200 group-hover:text-indigo-600 text-[10px]"></i>
                    </div>
                  </button>
                </div>

                <button
                  onClick={() => setShowOnboarding(false)}
                  className="w-full text-xs font-bold text-gray-300 uppercase tracking-widest hover:text-gray-900 transition-colors"
                >
                  Cancel
                </button>
              </>
            ) : (
              <div className="text-center space-y-6 py-6 animate-in fade-in zoom-in">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center text-white mx-auto shadow-lg">
                  <i className="fas fa-check text-2xl"></i>
                </div>
                <div className="space-y-1">
                  <h3 className="text-xl font-bold text-gray-900 tracking-tight">Everything is automatic</h3>
                  <p className="text-sm text-gray-500 font-medium">
                    Funds will flow to your account as friends pay.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className={`${isCompact ? 'max-w-full' : 'max-w-4xl mx-auto'} space-y-8`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <button
              onClick={onBackToHome}
              className={`flex items-center space-x-2 text-gray-400 hover:text-black transition-colors ${isCompact ? 'text-[9px]' : 'text-xs'} font-bold uppercase tracking-widest`}
            >
              <i className="fas fa-chevron-left text-[9px]"></i>
              <span>{isCompact ? 'Back' : 'Back to main'}</span>
            </button>
          </div>
          {(!isCompact || request.friends.length < 3) && <Logo size="sm" />}
        </div>

        <div className="space-y-12">
          <div className="space-y-8">
            <div className="flex justify-between items-end">
              <div className="space-y-1">
                <h1 className={`${isCompact ? 'text-2xl' : 'text-3xl'} font-bold text-gray-900 tracking-tight`}>Trip split</h1>
                <p className="text-sm text-gray-400 font-medium tracking-tight">{booking.title}</p>
              </div>
              <div className="text-right space-y-1">
                <p className="text-[10px] font-bold text-gray-300 uppercase tracking-widest">Collected</p>
                <p className="text-3xl font-bold text-gray-900 tracking-tight">${totalCollected.toFixed(0)}</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="h-2 bg-gray-50 rounded-full overflow-hidden">
                <div
                  className="h-full bg-black transition-all duration-1000 ease-out"
                  style={{ width: `${(totalPaidCount / (request.friends.length + 1)) * 100}%` }}
                />
              </div>
              <div className="flex justify-between text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                <span>{totalPaidCount} of {request.friends.length + 1} settled</span>
                <span>{Math.round((totalPaidCount / (request.friends.length + 1)) * 100)}%</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-8">
              <div className="space-y-4">
                <h2 className="text-[10px] font-bold text-gray-300 uppercase tracking-widest ml-1">Participants</h2>
                <div className="space-y-2">
                  <div className="p-5 bg-white border border-gray-100 rounded-2xl flex items-center justify-between shadow-sm">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center font-bold text-xs">A</div>
                      <div>
                        <p className="text-sm font-bold text-gray-900 tracking-tight">You (Organizer)</p>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Paid share</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-200 text-[9px] font-bold uppercase tracking-widest">
                      <span>Paid</span>
                      <i className="fas fa-check-circle"></i>
                    </div>
                  </div>

                  {request.friends.map((friend) => (
                    <div
                      key={friend.id}
                      onClick={() => handleSimulateItem(friend)}
                      className="p-5 bg-white border border-gray-100 rounded-2xl flex items-center justify-between shadow-sm group hover:border-gray-200 transition-all cursor-pointer"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 rounded-full bg-gray-50 text-gray-400 flex items-center justify-center font-bold text-xs border border-gray-50">{friend.name[0]}</div>
                        <div>
                          <p className="text-sm font-bold text-gray-900 tracking-tight">{friend.name}</p>
                          {editingId === friend.id ? (
                            <div className="flex items-center space-x-2 mt-1">
                              <input
                                autoFocus
                                type="number"
                                value={tempAmount}
                                onChange={(e) => setTempAmount(e.target.value)}
                                className="w-16 text-[10px] font-bold bg-gray-50 border border-gray-200 rounded px-1 py-0.5 focus:outline-none focus:border-black"
                              />
                              <button
                                onClick={(e) => { e.stopPropagation(); saveEdit(); }}
                                className="text-[9px] font-bold text-indigo-600 uppercase tracking-widest"
                              >
                                Done
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">${friend.amount.toFixed(0)} share</p>
                              {friend.status !== 'paid' && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); startEditing(friend); }}
                                  className="text-[8px] font-bold text-gray-500 uppercase tracking-widest hover:text-black transition-colors"
                                >
                                  Edit
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        {friend.status === 'paid' ? (
                          <div className="flex items-center space-x-2 text-green-500 text-[9px] font-bold uppercase tracking-widest">
                            <span>Settled</span>
                            <i className="fas fa-check-circle"></i>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-5">
                            <span className="text-[9px] font-bold text-gray-200 uppercase tracking-widest">Awaiting</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                triggerToast(`Reminder sent to ${friend.name}.`);
                              }}
                              className="text-[9px] font-bold text-black uppercase tracking-widest underline underline-offset-4 decoration-2"
                            >
                              Remind
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {webPaymentsEnabled && (
                <div className="p-6 bg-gray-50 rounded-2xl flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
                  <div className="space-y-0.5 text-center md:text-left">
                    <p className="text-sm font-bold text-gray-900 tracking-tight">Manual Share</p>
                    <p className="text-xs text-gray-500 font-medium">Copy your group's magic link.</p>
                  </div>
                  <button
                    onClick={() => triggerToast("Link copied.")}
                    className="flex items-center space-x-3 px-5 py-2.5 bg-white border border-gray-200 rounded-xl hover:border-black transition-all"
                  >
                    <i className="fas fa-copy text-xs text-gray-400"></i>
                    <span className="text-[10px] font-bold text-gray-900 uppercase tracking-widest">Copy Link</span>
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-8">
              <section className="space-y-4">
                <h3 className="text-[10px] font-bold text-gray-300 uppercase tracking-widest ml-1">About Clero</h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 flex-shrink-0">
                      <i className="fas fa-bolt text-[10px]"></i>
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed font-medium">Instant payouts to your account as friends settle their shares.</p>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 flex-shrink-0">
                      <i className="fas fa-shield-halved text-[10px]"></i>
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed font-medium">GroupLock™ protects all funds until the booking is final.</p>
                  </div>
                </div>
              </section>

              <section className="pt-8 border-t border-gray-50">
                <button
                  onClick={() => onFriendView(request.friends[0].name)}
                  className="w-full py-2 text-[9px] font-bold text-gray-300 uppercase tracking-widest hover:text-black transition-colors"
                >
                  Guest experience
                </button>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CleroSession;
